import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadAssessmentsAndContacts } from 'js/reducers/share';

const ITEMS_PER_PAGE = 10;

const useInfiniteScrollAssessments = (
    forceReloadQueries,
    orderBy = [{ field: 'createdAt', direction: 'DESC' }],
    filter = null,
    thereAreFiltersSet
) => {
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const observerRef = useRef();
    const initialLoadRef = useRef(false);
    const loadMoreRef = useRef(false);

    const dispatch = useDispatch();
    const assessmentPlans = useSelector(state => state.app.share.assessmentPlans);

    const fetchInitialData = useCallback(async () => {
        if (initialLoadRef.current) return;
        initialLoadRef.current = true;

        setLoading(true);
        setError(null);
        setOffset(0);
        setHasMore(true);

        try {
            const result = await dispatch(
                loadAssessmentsAndContacts({
                    limit: thereAreFiltersSet ? 100 : ITEMS_PER_PAGE,
                    offset: 0,
                    append: false,
                    orderBy,
                    filter,
                })
            );

            if (result && result.planList) {
                setHasMore(result.planList.length === ITEMS_PER_PAGE);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            setError(err);
            console.error('Error fetching initial assessments:', err);
        } finally {
            setLoading(false);
            initialLoadRef.current = false;
        }
    }, [dispatch, orderBy, filter, thereAreFiltersSet]);

    useEffect(() => {
        initialLoadRef.current = false;
        fetchInitialData();
    }, [forceReloadQueries, thereAreFiltersSet]);

    const loadMore = useCallback(async () => {
        if (isLoadingMore || !hasMore || loading || loadMoreRef.current) return;

        loadMoreRef.current = true;
        setIsLoadingMore(true);
        const newOffset = offset + ITEMS_PER_PAGE;

        try {
            const result = await dispatch(
                loadAssessmentsAndContacts({
                    limit: ITEMS_PER_PAGE,
                    offset: newOffset,
                    append: true,
                    orderBy,
                    filter,
                })
            );

            if (result && result.planList) {
                if (result.planList.length > 0) {
                    setOffset(newOffset);
                    setHasMore(result.planList.length === ITEMS_PER_PAGE);
                } else {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error loading more assessments:', err);
        } finally {
            setIsLoadingMore(false);
            loadMoreRef.current = false;
        }
    }, [dispatch, isLoadingMore, hasMore, loading, offset, orderBy, filter]);

    const lastElementRef = useCallback(
        node => {
            if (loading || isLoadingMore) return;
            if (observerRef.current) observerRef.current.disconnect();

            observerRef.current = new IntersectionObserver(
                entries => {
                    // Only trigger on the first entry that's intersecting and ensure we have more data to load
                    if (entries[0].isIntersecting && hasMore && !isLoadingMore && !loading && !initialLoadRef.current) {
                        loadMore();
                    }
                },
                {
                    threshold: 0.1,
                    rootMargin: '50px',
                }
            );

            if (node) observerRef.current.observe(node);
        },
        [loading, isLoadingMore, hasMore, loadMore]
    );

    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    return {
        assessmentPlans,
        loading,
        error,
        hasMore,
        isLoadingMore,
        lastElementRef,
        loadMore,
    };
};

export default useInfiniteScrollAssessments;
